let sequence = 0

const clone = value => JSON.parse(JSON.stringify(value))

const makeId = prefix => {
  sequence += 1
  return `${prefix}-${Date.now().toString(36)}-${sequence.toString(36)}`
}

export const fieldTypes = [
  { value: 'number', label: 'Valeur numérique' },
  { value: 'text', label: 'Texte court' },
  { value: 'long_text', label: 'Compte rendu' },
  { value: 'choice', label: 'Choix dans une liste' },
  { value: 'boolean', label: 'Oui / Non' },
  { value: 'date', label: 'Date' },
]

export function createField(type = 'number') {
  const field = {
    id: makeId('field'),
    label: '',
    type,
    unit: '',
    required: false,
    placeholder: '',
    reference: { min: '', max: '', text: '' },
    critique: { min: '', max: '' },   // LAB-007 : seuils critiques (alerte à la validation)
    options: [],
  }

  if (type === 'choice') field.options = ['Positif', 'Négatif']
  return field
}

export function createSection(title = 'Résultats') {
  return {
    id: makeId('section'),
    title,
    description: '',
    fields: [createField()],
  }
}

export function createStructuredMeta() {
  return {
    schema_version: 2,
    layout: 'structured',
    attachments_enabled: false,
    sections: [createSection()],
  }
}

export function createTableMeta() {
  return {
    schema_version: 2,
    layout: 'table',
    attachments_enabled: false,
    table_header_name: '',
    rows: [
      { cells: [{ value: 'Paramètre' }, { value: 'Résultat' }, { value: 'Unité' }, { value: 'Valeurs de référence' }] },
      { cells: [{ value: '' }, { value: '' }, { value: '' }, { value: '' }] },
    ],
  }
}

export function createDocumentMeta() {
  return {
    schema_version: 2,
    layout: 'document',
    attachments_enabled: true,
    document: { multiple: true, require_interpretation: false },
  }
}

function normalizeDocument(meta) {
  const normalized = clone(meta)
  normalized.schema_version = 2
  normalized.layout = 'document'
  // Le format Document est une activation explicite : la pièce jointe est le résultat.
  normalized.attachments_enabled = true
  const doc = normalized.document && typeof normalized.document === 'object' ? normalized.document : {}
  normalized.document = {
    multiple: typeof doc.multiple === 'boolean' ? doc.multiple : true,
    require_interpretation: Boolean(doc.require_interpretation),
  }
  return normalized
}

function normalizeStructured(meta) {
  const normalized = clone(meta)
  normalized.schema_version = 2
  normalized.layout = 'structured'
  normalized.attachments_enabled = meta.attachments_enabled === true
  normalized.sections = Array.isArray(normalized.sections) && normalized.sections.length
    ? normalized.sections
    : [createSection()]

  normalized.sections.forEach(section => {
    if (!section.id) section.id = makeId('section')
    if (!Array.isArray(section.fields) || !section.fields.length) section.fields = [createField()]
    section.fields.forEach(field => {
      if (!field.id) field.id = makeId('field')
      if (!field.type) field.type = 'text'
      if (!field.reference) field.reference = { min: '', max: '', text: '' }
      if (!field.critique || typeof field.critique !== 'object') field.critique = { min: '', max: '' }
      if (!Array.isArray(field.options)) field.options = []
      if (typeof field.required !== 'boolean') field.required = false
    })
  })
  return normalized
}

export function normalizeAnalysisMeta(meta) {
  if (!meta || typeof meta !== 'object') return createStructuredMeta()
  if (meta.layout === 'document') return normalizeDocument(meta)
  if (meta.layout === 'structured' || Array.isArray(meta.sections)) return normalizeStructured(meta)

  const legacy = clone(meta)
  legacy.schema_version = legacy.schema_version || 1
  legacy.layout = 'table'
  legacy.attachments_enabled = meta.attachments_enabled === true
  legacy.rows = Array.isArray(legacy.rows) ? legacy.rows : createTableMeta().rows
  return legacy
}

export function isAnalysisMetaReady(meta) {
  const normalized = normalizeAnalysisMeta(meta)
  if (normalized.layout === 'document') return true
  if (normalized.layout === 'structured') {
    return normalized.sections.length > 0 && normalized.sections.every(section => (
      String(section.title || '').trim().length > 0 &&
      Array.isArray(section.fields) && section.fields.length > 0 &&
      section.fields.every(field => String(field.label || '').trim().length > 0)
    ))
  }

  const rows = normalized.rows || []
  return rows.length > 1 && rows[0].cells.every(cell => cell.merged || String(cell.value || '').trim().length > 0)
}

export function createResultPayload(meta) {
  const normalized = normalizeAnalysisMeta(meta)
  if (normalized.layout !== 'structured') return clone(normalized.rows || [])

  return normalized.sections.map(section => ({
    section_id: section.id,
    title: section.title,
    fields: section.fields.map(field => ({
      field_id: field.id,
      label: field.label,
      type: field.type,
      unit: field.unit || '',
      required: Boolean(field.required),
      options: clone(field.options || []),
      value: '',
      reference: clone(field.reference || {}),
      critique: clone(field.critique || {}),
    })),
  }))
}

// Certains modèles historiques contiennent ce champ dans la grille de saisie.
// L'interprétation officielle est saisie séparément lors de la validation biologique.
export function technicianEntrySections(sections) {
  if (!Array.isArray(sections)) return []
  return sections.map(section => {
    if (!section || !Array.isArray(section.fields)) return section
    const fields = section.fields.filter(field => {
      const label = String(field && field.label || '').trim().toLocaleLowerCase('fr-FR')
        .normalize('NFD').replace(/[\u0300-\u036f]/g, '').replace(/\s+/g, ' ')
      return label !== 'commentaire du biologiste' && label !== 'interpretation du biologiste'
    })
    return fields.length === section.fields.length ? section : { ...section, fields }
  }).filter(section => section && (!Array.isArray(section.fields) || section.fields.length > 0))
}
